import { useEffect } from "react";
import { site } from "@/config/site";

function upsertMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const [attr, value] = selector.match(/\[(name|property)="(.+?)"\]/).slice(1);
    el.setAttribute(attr, value);
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
}

const OG_IMAGE = `${site.domain}/og-image.jpg`;

export function SEO({ title, description, path = "/", jsonLd = [], noindex = false }) {
  const url = `${site.domain}${path}`;
  const ldKey = JSON.stringify(jsonLd);
  useEffect(() => {
    document.title = title;
    upsertMeta('[name="description"]', { content: description });
    upsertMeta('[name="robots"]', {
      content: noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1",
    });
    upsertMeta('[property="og:title"]', { content: title });
    upsertMeta('[property="og:description"]', { content: description });
    upsertMeta('[property="og:url"]', { content: url });
    upsertMeta('[property="og:type"]', { content: "website" });
    upsertMeta('[property="og:site_name"]', { content: site.name });
    upsertMeta('[property="og:locale"]', { content: "en_GB" });
    upsertMeta('[property="og:image"]', { content: OG_IMAGE });
    upsertMeta('[property="og:image:width"]', { content: "1200" });
    upsertMeta('[property="og:image:height"]', { content: "630" });
    upsertMeta('[property="og:image:alt"]', { content: "Made-to-measure wooden venetian blinds in a bright modern Birmingham home" });
    upsertMeta('[name="twitter:card"]', { content: "summary_large_image" });
    upsertMeta('[name="twitter:title"]', { content: title });
    upsertMeta('[name="twitter:description"]', { content: description });
    upsertMeta('[name="twitter:image"]', { content: OG_IMAGE });
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);
    const scripts = [];
    JSON.parse(ldKey).forEach((data, i) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.id = `page-jsonld-${i}`;
      s.text = JSON.stringify(data);
      document.head.appendChild(s);
      scripts.push(s);
    });
    return () => scripts.forEach((s) => s.remove());
  }, [title, description, url, ldKey, noindex]);
  return null;
}

export const localBusinessLd = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: site.legalName,
  url: site.domain,
  image: OG_IMAGE,
  email: site.email,
  telephone: site.phone.display,
  address: {
    "@type": "PostalAddress",
    streetAddress: `${site.address.line1}, ${site.address.line2}`,
    addressLocality: site.address.city,
    postalCode: site.address.postcode,
    addressCountry: "GB",
  },
  areaServed: ["Birmingham", "Solihull", "Sutton Coldfield", "Walsall", "West Bromwich", "Dudley", "Halesowen", "West Midlands"],
  sameAs: [site.social.facebook, site.social.instagram, site.social.tiktok].filter(Boolean),
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "09:00",
      closes: "17:00",
    },
  ],
};

export const organizationLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: site.legalName,
  url: site.domain,
  logo: `${site.domain}/logo.png`,
  email: site.email,
  telephone: site.phone.display,
  sameAs: [site.social.facebook, site.social.instagram, site.social.tiktok].filter(Boolean),
};

export const websiteLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: site.name,
  url: site.domain,
};

export const breadcrumbLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${site.domain}${it.path}`,
  })),
});

export const faqLd = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});
